import { TranscriptParagraph } from './Transcript'

// The fields used from the package's `InconsistenciesInsightItem`, which fits it; also allows the
// `null` offsets the backend sends.
type TranscriptStatement = {
    type: 'transcript'
    offset?: number | null
    is_root_statement?: boolean
    text: string
    highlight_turns?: { text: string; offset?: number }[] | null
    highlight_spans?: string[] | null
}

export type InconsistencyStatement = TranscriptStatement | { type: 'file' }

export type InconsistencyItem = {
    uuid: string
    title: string
    statements: InconsistencyStatement[]
}

export type InlineInconsistency = {
    uuid: string
    title: string
}

// `times[i]` is the time of `items[i]`, sorted ascending.
export type PreparedInconsistencies = {
    times: readonly number[]
    items: readonly InlineInconsistency[]
    earliest: number
    latest: number
}

export const NO_INCONSISTENCIES: readonly InlineInconsistency[] = Object.freeze([])

const EMPTY_PREPARED: PreparedInconsistencies = Object.freeze({
    times: Object.freeze([]),
    items: NO_INCONSISTENCIES,
    earliest: Infinity,
    latest: -Infinity,
})

const getQuoteStatement = ({ statements }: InconsistencyItem) => {
    const transcriptStatements = statements.filter((statement): statement is TranscriptStatement => statement.type === 'transcript')

    return transcriptStatements.find((statement) => statement.is_root_statement) ?? transcriptStatements[0]
}

const getAnchorTime = (item: InconsistencyItem) => getQuoteStatement(item)?.offset

// What a card click in the insights panel highlights: only a single root transcript statement
// (`filterRootStatement`), its `getHighlightTurns`, else the statement text as `scrollToSection` does.
export const getInconsistencyQuote = ({ statements }: InconsistencyItem) => {
    const roots = statements.filter(
        (statement): statement is TranscriptStatement => statement.type === 'transcript' && Boolean(statement.is_root_statement),
    )
    const statement = roots.length === 1 ? roots[0] : undefined
    if (!statement?.offset) {
        return null
    }

    const { offset, text, highlight_turns, highlight_spans } = statement
    const turns = highlight_turns?.length
        ? highlight_turns
        : highlight_spans?.length
          ? highlight_spans.map((span) => ({ text: span }))
          : text
            ? [{ text }]
            : []

    return { offset, turns }
}

export const prepareInconsistencies = (list: readonly InconsistencyItem[] | undefined): PreparedInconsistencies => {
    const timed: { time: number; inconsistency: InlineInconsistency }[] = []
    for (const item of list ?? []) {
        const time = getAnchorTime(item)
        // Missing, `null` and `0.0` all mean "unknown"; a button must never sit under the wrong paragraph.
        if (time) {
            timed.push({ time, inconsistency: { uuid: item.uuid, title: item.title } })
        }
    }
    if (timed.length === 0) {
        return EMPTY_PREPARED
    }

    // Stable sort: equal times keep input order, which is the order the button steps through.
    timed.sort((a, b) => a.time - b.time)
    const times = timed.map(({ time }) => time)

    return { times, items: timed.map(({ inconsistency }) => inconsistency), earliest: times[0], latest: times[times.length - 1] }
}

// Index of the first time >= `target`.
const lowerBound = (times: readonly number[], target: number) => {
    let low = 0
    let high = times.length
    while (low < high) {
        const middle = (low + high) >>> 1
        if (times[middle] < target) {
            low = middle + 1
        } else {
            high = middle
        }
    }

    return low
}

export const getParagraphInconsistencies = (
    { times, items, earliest, latest }: PreparedInconsistencies,
    from: number,
    to: number,
): readonly InlineInconsistency[] => {
    if (to <= earliest || from > latest) {
        return NO_INCONSISTENCIES
    }

    const start = lowerBound(times, from)
    const end = lowerBound(times, to)

    return start < end ? items.slice(start, end) : NO_INCONSISTENCIES
}

type ParagraphTimes = Pick<TranscriptParagraph, 'startTime' | 'endTime'>

// A paragraph owns the gap before it, so a sentence starting between two paragraphs goes to the
// one that holds its words. No `endTime` means no timed words yet: it owns nothing, which also
// makes live times wait for their paragraph.
export const getParagraphWindow = (paragraph: ParagraphTimes, previous?: ParagraphTimes) => {
    const from = previous ? (previous.endTime ?? paragraph.startTime) : 0
    if (paragraph.endTime === null || from === null) {
        return null
    }

    return { from, to: paragraph.endTime }
}
